import { useEffect, useRef, useState } from 'react'
import { ExpertView, connectSdkRealtime, type SdkRealtimeConnection, type SdkRealtimeMotionRuntime, type SdkVoiceState } from '../experts-alive-sdk/react'
import type { GameState, Page } from '../types'
import { apiIdentity } from '../api'
import { t } from '../i18n'
import { guideDetails, guideSlugs } from './catalog'
import { screenSummary } from './screenSummary'
import { tourSections, tourPrompt, tourSentences, TourPlayback } from './appTour'
import { tourLabels } from './tourLabels'

type Request = {epoch:number;kind:'screen'|'tour'|'listen';index:number}
export function DigitalGuide({page,state,onClose,initialTour=false,gameContext=''}:{page:Page;state:GameState;onClose:()=>void;initialTour?:boolean;gameContext?:string}) {
  const [tour,setTour]=useState(initialTour)
  const [index,setIndex]=useState(0)
  const [status,setStatus]=useState<'playing'|'paused'|'complete'>(initialTour?'playing':'paused')
  const [request,setRequest]=useState<Request>({epoch:0,kind:initialTour?'tour':'screen',index:0})
  const [question,setQuestion]=useState('')
  const [answer,setAnswer]=useState('')
  const [userText,setUserText]=useState('')
  const [voiceState,setVoiceState]=useState<SdkVoiceState>('connecting')
  const [runtime,setRuntime]=useState<SdkRealtimeMotionRuntime|null>(null)
  const [error,setError]=useState('')
  const connection=useRef<SdkRealtimeConnection|null>(null)
  const stopSession=useRef<()=>void>(()=>{})
  const playback=useRef<TourPlayback|null>(null)
  const dialog=useRef<HTMLElement>(null)
  const closeButton=useRef<HTMLButtonElement>(null)
  const human=guideDetails[state.language]??guideDetails['en-US']
  const slug=guideSlugs[state.language]??'arthur'
  const labels=tourLabels(state.language)
  const intro=screenSummary(page,state)

  // Preserve the mounted portrait when switching panels. Only the voice session
  // is replaced on manual interruptions so delayed audio cannot leak through.
  const restart=(kind:Request['kind'],at=index)=>{
    stopSession.current()
    setAnswer('');setUserText('');setError('');setVoiceState('connecting')
    setStatus(kind==='tour'?'playing':'paused')
    setRequest(previous=>({epoch:previous.epoch+1,kind,index:at}))
  }
  const close=()=>{
    if(tour&&!initialTour){setTour(false);restart('screen')}
    else{stopSession.current();onClose()}
  }
  const play=(at:number)=>{setIndex(at);restart('tour',at)}

  useEffect(()=>{
    const previous=document.activeElement as HTMLElement|null
    return()=>{previous?.focus()}
  },[])
  useEffect(()=>{closeButton.current?.focus()},[tour])

  useEffect(()=>{
    if(!runtime)return
    let active=true
    let live:SdkRealtimeConnection|null=null
    let sequence:TourPlayback|null=null
    const internalPrompts=new Set<string>()
    const dispose=()=>{
      active=false;sequence?.dispose();playback.current=null
      live?.disconnect()
      if(connection.current===live)connection.current=null
    }
    stopSession.current=dispose
    setVoiceState('connecting');setError('')
    const fail=(message:string)=>{if(active){sequence?.dispose();setStatus('paused');setError(message);setVoiceState('error')}}
    const narrate=(at:number)=>{
      // Defer until the SDK finishes its playback-stopped callback. Never advance
      // on response generation alone: device audio must finish first.
      queueMicrotask(()=>{
        if(!active)return
        const prompt=tourPrompt(at,human.language,state.worldId)
        internalPrompts.add(prompt)
        setIndex(at);setAnswer('');setUserText('')
        if(!live?.sendText(prompt))fail('The tour paused before the next section. Choose a section to try again.')
      })
    }
    if(request.kind==='tour'){
      sequence=new TourPlayback(request.index,narrate,()=>{if(active)setStatus('complete')})
      playback.current=sequence
    }
    void (async()=>{
      try{
        const identity=await apiIdentity()
        if(!active)return
        if(!identity)throw new Error('The cloud voice service is not configured for this build.')
        const connected=await connectSdkRealtime({
          deploymentKey:identity.token,sessionEndpoint:`/api${identity.user?'/user':''}/experts-session`,humanId:human.id,
          language:state.language,platform:'react',platformId:window.location.origin,
          resolution:'high',tutorName:human.name,voice:'ash',group:'Lucky',motionRuntime:runtime,
          autoListen:request.kind!=='tour',
          conversationInstructions:`You are ${human.name}, the friendly Lucky Estates guide. Speak in ${human.language}. The current game screen is ${page}. Screen summary: ${intro} The player has ${state.rolls} rolls, ${state.coins} coins, ${state.shields} shields, and rank ${state.totalUpgrades}. ${gameContext} App reference: ${tourSections.map((section,at)=>`${section.page}: ${tourSentences(at,state.worldId).join(' ')}`).join('\n')} When asked to narrate a tour section, provide exactly its three source sentences, naturally translated into ${human.language}, with no extra remarks. Never ask the player to provide the summary. Otherwise answer questions concisely about the game. Never claim an action happened unless the player did it. The currently selected tour section is ${tourSections[request.index].page}.`,
          greetingPrompt:request.kind==='listen'?undefined:request.kind==='tour'?tourPrompt(request.index,human.language,state.worldId):`Speak the screen introduction now in exactly two short declarative sentences in ${human.language}. Do not ask the player to provide anything, mention prompts or sentence counts, or add a greeting or question. Source introduction: ${intro}`,
          onAssistantText:text=>{if(active)setAnswer(text)},
          onUserText:text=>{if(active&&!internalPrompts.has(text))setUserText(text)},
          onState:value=>{if(active){setVoiceState(value);sequence?.state(value)}},
          onError:fail,
          onTransportInterrupted:()=>fail('Voice connection interrupted. Reconnect to continue.'),
        })
        if(!active){connected.disconnect();return}
        live=connected;connection.current=connected
      }catch(cause){fail(cause instanceof Error?cause.message:String(cause))}
    })()
    return dispose
  },[runtime,request])

  const reply=()=>{
    const value=question.trim()
    if(!value)return
    playback.current?.dispose();setStatus('paused')
    if(connection.current?.sendText(value)){setQuestion('');setUserText(value)}
    else setError('Press Speak to pause the guide, then send your question when listening is ready.')
  }
  const ready=voiceState!=='connecting'&&voiceState!=='error'
  const previewOnly=error==='The cloud voice service is not configured for this build.'
  return <div className="modal-backdrop"><section ref={dialog} className={`guide-modal ${tour?'app-tour-modal':''}`} role="dialog" aria-modal="true" aria-labelledby="guide-title" onKeyDown={event=>{
    if(event.key==='Escape'){event.stopPropagation();close()}
    if(event.key==='Tab'){
      const items=dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), [tabindex="0"]')
      if(!items?.length)return
      const first=items[0],last=items[items.length-1]
      if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus()}
      else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus()}
    }
  }}>
    <div className="human-pane"><div className="human-video"><ExpertView poolKey={`${human.id}:${state.language}`} poolGroupKey="lucky-guides" assetManifestUrl={`https://d32q4q90fh3ret.cloudfront.net/digital-humans/sdk/v1/humans/${slug}/asset-manifest.json`} settingsUrl="/assets/experts-alive/approved-lip-sync-settings.json" locale={state.language} plan="enterprise" resolution="auto" onRuntimeReady={setRuntime} onError={cause=>setError(cause.message)}/><span>{previewOnly?'PREVIEW':voiceState.toUpperCase()}</span></div><strong>{human.name}</strong><small>Your Lucky Estates guide</small></div>
    <div className="guide-copy">
      <header className="guide-heading"><h2 id="guide-title">{tour?labels.title:t(state.language,'explain')}</h2><button ref={closeButton} className="guide-close" onClick={close}>{t(state.language,'close')} ×</button></header>
      {tour?<>
        <p className="tour-instruction">Ten sections, three sentences each. Choose any section to hear it again, or speak to your guide.</p>
        <nav className="tour-sections" aria-label="App tour sections">{tourSections.map((section,at)=><button key={section.key} aria-current={at===index?'step':undefined} className={at===index?'current':''} onClick={()=>play(at)}><span>{String(at+1).padStart(2,'0')}</span>{t(state.language,section.key)}</button>)}</nav>
        <div className="tour-progress" role="status"><b>{index+1} / {tourSections.length} · {t(state.language,tourSections[index].key)}</b><span>{status==='complete'?labels.complete:status==='paused'?labels.paused:voiceState==='connecting'?'Connecting…':voiceState==='speaking'?'Playing…':'Preparing…'}</span></div>
        <div className="tour-reading"><p>{answer||tourSentences(index,state.worldId).join(' ')}</p></div>
        <div className="tour-actions"><button onClick={()=>play(index)}>↻ {labels.replay}</button>{status==='paused'&&<button onClick={()=>play(index)}>▶ {labels.resume}</button>}</div>
      </>:<>
        <p className="eyebrow">{page}</p><p className="guide-summary">{intro}</p>
        <div className="recommend"><b>Recommended next step</b><span>{page==='Play'?'Press ROLL when you are ready to move.':page==='My city'?'Improve the highlighted landmark when you have enough coins.':'Review the available actions and choose the one you need.'}</span></div>
        <button className="entire-app-button" onClick={()=>{setTour(true);play(0)}}><span>▤</span><b>{labels.title}</b><span>→</span></button>
        {answer&&<div className="transcript"><b>{human.name.toUpperCase()}</b>{answer}</div>}
      </>}
      {userText&&<div className="user-transcript"><b>YOU</b>{userText}</div>}
      {error&&<div className="voice-error" role="alert">{error}{!previewOnly&&<button onClick={()=>restart(tour?'tour':'screen')}>{labels.reconnect}</button>}</div>}
      <div className="guide-interaction"><div className="guide-conversation"><button className="guide-speak" disabled={!ready} onClick={()=>restart('listen')}>◉ {labels.speak}</button><span>{voiceState==='listening'?'Listening — ask your question.':'Speak pauses the explanation so you can ask a question.'}</span></div>
      <form onSubmit={event=>{event.preventDefault();reply()}}><input aria-label={labels.ask} value={question} onChange={event=>setQuestion(event.target.value)} placeholder={labels.ask}/><button disabled={!ready||voiceState==='speaking'||!question.trim()}>{labels.ask}</button></form></div>
    </div>
  </section></div>
}
